// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import registerForEvent from "@mcmc/App/Handlers/Events/RegisterForEvent";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import { SystemListCollectionModel } from "@mcmc/App/Services/SystemListService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getEventData } from "@mcmc/App/Store/Selectors/ProductDetailsSelectors";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import { EventRegistrationFormState } from "@mcmc/App/Widgets/EventRegistration/EventRegistrationForm/EventRegistrationForm.helper";
import ReCaptcha, { ReCaptchaStyles } from "@mcmc/Overrides/Components/ReCaptcha";
import { AForm } from "@mcmc/Overrides/Widgets/CreateAccount/CreateAccount.form-helper";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import Notify from "@mcmc/XDL/Components/Notify";
import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type OwnProps = HasProduct & {
    countries: CountryModel[] | undefined;
    contactUsOptions: SystemListCollectionModel | null;
    initialState: [EventRegistrationFormState, React.Dispatch<React.SetStateAction<EventRegistrationFormState>>];
    goToEventDetailsPage: () => void;
    successCallback: () => boolean;
};

const mapDispatchToProps = {
    registerForEvent,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
};

const mapStateToProps = (state: McMcApplicationState, props: OwnProps) => ({
    eventData: getEventData(state, props.product),
    customSettings: getSettingsCollection(state).customSettings,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface EventRegistrationFormStyles {
    form?: InjectableCss;
    requiredFieldsMessage?: TypographyPresentationProps;
    inputsWrapper?: InjectableCss;
    textField?: TextFieldPresentationProps;
    selectField?: SelectPresentationProps;
    actionsWrapper?: InjectableCss;
    errorMessageWrapper?: InjectableCss;
    recaptcha?: ReCaptchaStyles;
}

export const eventRegistrationFormStyles: EventRegistrationFormStyles = {
    form: {
        css: css`
            width: 100%;
            max-width: 770px;
            margin: auto;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 370px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    requiredFieldsMessage: {
        size: 14,
        lineHeight: "16px",
        css: css`
            display: block;
            width: 100%;
            padding: 8px 0;
            letter-spacing: 0.2px;
        `,
    },
    inputsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin: 0 -15px;
        `,
    },
    textField: {
        cssOverrides: {
            formField: [
                css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                    flex: 1 1 50%;
                    max-width: 400px;
                    padding: 15px;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    flex: 1 1 100%;
                                `,
                            ],
                            "max",
                        )}
                `,
            ],
        },
    },
    actionsWrapper: {
        css: css`
            margin-top: 15px;
            padding-top: 30px;
            border-top: 1px solid ${getColor("common.ui2")};

            button {
                padding: 0 40px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0 25px;
                            `,
                        ],
                        "max",
                    )}

                &:first-child {
                    margin-right: 10px;
                }
            }
        `,
    },
    errorMessageWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    recaptcha: {
        wrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
    },
};

eventRegistrationFormStyles.selectField = eventRegistrationFormStyles.textField as SelectPresentationProps;

const styles = eventRegistrationFormStyles;
const StyledForm = getStyledWrapper("form");

const EventRegistrationForm = ({
    product,
    countries,
    contactUsOptions,
    initialState,
    registerForEvent,
    goToEventDetailsPage,
    successCallback,
    customSettings,
    validateReCaptcha,
}: Props) => {
    const [formState, setFormState] = initialState;
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const form = new AForm<EventRegistrationFormState>(formState, setFormState);
    const { fields, isSubmitted, isValid } = form.state;
    const { checkReCaptchaForEventRegistrationOnClientSide } = customSettings;
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        if (form.state.isSubmitted && !form.state.isValid) {
            initializeScrollTo("[data-test-selector*='-error']", -50);
        }
    }, [form.state.isSubmitted]);

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!form.state.isSubmitted) {
            form.setSubmitted();
        }
        const selectedCountry = countries && countries[fields.country.value - 1];
        const isReCaptchaValid = await validateReCaptcha({ location: "EventRegistrationForm", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }
        if (form.validate()) {
            setIsSubmitting(true);
            registerForEvent({
                ProductNumber: product.productNumber,
                FirstName: fields.firstName.value,
                LastName: fields.lastName.value,
                Title: fields.title.value,
                Company: fields.company.value,
                StreetAddress: fields.streetAddress.value,
                Address2: fields.addressContinued.value,
                City: fields.city.value,
                Country: selectedCountry?.abbreviation || "",
                State: fields.state.value,
                ZipOrPostalCode: fields.zipCode.value,
                BusinessPhone: fields.businessPhone.value,
                ContactOrMobilePhone: fields.mobilePhone.value,
                FaxNumber: fields.faxNumber.value,
                Email: fields.email.value,
                HowCanWeContactYou: fields.howCanWeContactYou.value,
                CommentsAndSpecialRequests: fields.comments.value,
                PaymentMethod: fields.paymentMethod.value,
                POOrReferenceNumber: fields.poNumber.value,
                onSuccess: () => {
                    setIsSubmitting(false);
                    successCallback();
                },
                onError: error => {
                    setErrorMessage(error);
                    setIsSubmitting(false);
                },
            });
        }
    };

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <Typography {...styles.requiredFieldsMessage}>
                {translate("All fields are required except as indicated.")}
            </Typography>

            <StyledWrapper {...styles.inputsWrapper}>
                <TextField
                    {...fields.firstName.attrs}
                    value={fields.firstName.value}
                    onChange={e => form.onFieldChange(fields.firstName, e.currentTarget.value)}
                    error={isSubmitted && fields.firstName.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.lastName.attrs}
                    value={fields.lastName.value}
                    onChange={e => form.onFieldChange(fields.lastName, e.currentTarget.value)}
                    error={isSubmitted && fields.lastName.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.title.attrs}
                    value={fields.title.value}
                    onChange={e => form.onFieldChange(fields.title, e.currentTarget.value)}
                    error={isSubmitted && fields.title.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.company.attrs}
                    value={fields.company.value}
                    onChange={e => form.onFieldChange(fields.company, e.currentTarget.value)}
                    error={isSubmitted && fields.company.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.streetAddress.attrs}
                    value={fields.streetAddress.value}
                    onChange={e => form.onFieldChange(fields.streetAddress, e.currentTarget.value)}
                    error={isSubmitted && fields.streetAddress.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.addressContinued.attrs}
                    value={fields.addressContinued.value}
                    onChange={e => form.onFieldChange(fields.addressContinued, e.currentTarget.value)}
                    error={isSubmitted && fields.addressContinued.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.city.attrs}
                    value={fields.city.value}
                    onChange={e => form.onFieldChange(fields.city, e.currentTarget.value)}
                    error={isSubmitted && fields.city.error}
                    {...styles.textField}
                />
                <Select
                    {...fields.country.attrs}
                    value={fields.country.value}
                    onChange={e => form.onFieldChange(fields.country, Number(e.currentTarget.value))}
                    error={isSubmitted && fields.country.error}
                    {...styles.selectField}
                >
                    <option value={0}>{translate("Select One")}</option>
                    {countries &&
                        countries.map((o, i) => (
                            <option key={o.id} value={i + 1}>
                                {o.name}
                            </option>
                        ))}
                </Select>
                <Select
                    {...fields.state.attrs}
                    value={fields.state.value}
                    onChange={e => form.onFieldChange(fields.state, e.currentTarget.value)}
                    error={isSubmitted && fields.state.error}
                    {...styles.selectField}
                >
                    <option value="">{translate("Select One")}</option>
                    {countries &&
                        fields.country.value >= 0 &&
                        countries[fields.country.value - 1]?.states?.map(o => (
                            <option key={o.id} value={o.abbreviation}>
                                {o.name}
                            </option>
                        ))}
                </Select>
                <TextField
                    {...fields.zipCode.attrs}
                    value={fields.zipCode.value}
                    onChange={e => form.onFieldChange(fields.zipCode, e.currentTarget.value)}
                    error={isSubmitted && fields.zipCode.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.businessPhone.attrs}
                    value={fields.businessPhone.value}
                    onChange={e => form.onFieldChange(fields.businessPhone, e.currentTarget.value)}
                    error={isSubmitted && fields.businessPhone.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.mobilePhone.attrs}
                    value={fields.mobilePhone.value}
                    onChange={e => form.onFieldChange(fields.mobilePhone, e.currentTarget.value)}
                    error={isSubmitted && fields.mobilePhone.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.faxNumber.attrs}
                    value={fields.faxNumber.value}
                    onChange={e => form.onFieldChange(fields.faxNumber, e.currentTarget.value)}
                    error={isSubmitted && fields.faxNumber.error}
                    {...styles.textField}
                />
                <TextField
                    {...fields.email.attrs}
                    value={fields.email.value}
                    onChange={e => form.onFieldChange(fields.email, e.currentTarget.value)}
                    error={isSubmitted && fields.email.error}
                    {...styles.textField}
                />
                <Select
                    {...fields.howCanWeContactYou.attrs}
                    value={fields.howCanWeContactYou.value}
                    onChange={e => form.onFieldChange(fields.howCanWeContactYou, e.currentTarget.value)}
                    error={isSubmitted && fields.howCanWeContactYou.error}
                    {...styles.selectField}
                >
                    <option value="">{translate("Select One")}</option>
                    {contactUsOptions &&
                        contactUsOptions.values.map(o => (
                            <option key={o.name} value={o.name}>
                                {o.name}
                            </option>
                        ))}
                </Select>
                <TextField
                    {...fields.comments.attrs}
                    value={fields.comments.value}
                    onChange={e => form.onFieldChange(fields.comments, e.currentTarget.value)}
                    error={isSubmitted && fields.comments.error}
                    {...styles.textField}
                />
                <Select
                    {...fields.paymentMethod.attrs}
                    value={fields.paymentMethod.value}
                    onChange={e => form.onFieldChange(fields.paymentMethod, e.currentTarget.value)}
                    error={isSubmitted && fields.paymentMethod.error}
                    {...styles.selectField}
                >
                    <option value="">{translate("Select One")}</option>
                    <option value="Credit Card">{translate("Credit Card")}</option>
                    <option value="PO">{translate("PO")}</option>
                </Select>
                <TextField
                    {...fields.poNumber.attrs}
                    value={fields.poNumber.value}
                    onChange={e => form.onFieldChange(fields.poNumber, e.currentTarget.value)}
                    error={isSubmitted && fields.poNumber.error}
                    {...styles.textField}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                <Button
                    type="submit"
                    disabled={isSubmitting || (isSubmitted && !isValid)}
                    data-test-selector="SubmitButton"
                >
                    <EllipsisSpinner label={translate("Submit")} showSpinner={isSubmitting}></EllipsisSpinner>
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    disabled={isSubmitting}
                    data-test-selector="cancelButton"
                    onClick={goToEventDetailsPage}
                >
                    {translate("Cancel")}
                </Button>
            </StyledWrapper>
            <ReCaptcha
                isEnabled={checkReCaptchaForEventRegistrationOnClientSide.toLowerCase() === "true"}
                extendedStyles={styles.recaptcha}
                recaptchaRef={recaptchaRef}
                location="EventRegistrationForm"
            />
            {errorMessage && (
                <StyledWrapper {...styles.errorMessageWrapper}>
                    <Notify messageType="danger" body={errorMessage} hideCloseButton={true} />
                </StyledWrapper>
            )}
        </StyledForm>
    );
};

export default withProduct(connect(mapStateToProps, mapDispatchToProps)(EventRegistrationForm));
